import {
  AnalyticsFieldName,
  BookingAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { EditEventAnalyticsEvent } from "@/constants/analytics/Scheduling";
import type { TabSources } from "@/constants/analytics/Tabs";
import type { MixpanelProps } from "@/services/Analytics";
import { useAnalytics } from "@/services/Analytics";

interface TrackEventArgs {
  event: BookingAnalyticsEvent;
  source: BookingStep;
  value?: string;
}

interface TrackAlertEventArgs {
  event: BookingAnalyticsEvent;
  source: BookingAlertType;
}

const BookingAlertNameMap: Record<BookingAlertType, string> = {
  dismiss: "Leave booking modal",
  error: "Booking error modal",
  success: "Scheduled confirmation modal",
};

const AnalyticsScreenNameMap: Record<BookingStep, string> = {
  attendees: "Confirm attendees",
  availability: "Availability selection",
  confirmation: "Review appointment details",
  duration: "Time selection",
  optionalAttendees: "Add someone else",
  patient: "Choose patient",
  provider: "Choose provider",
  recurrence: "Recurrence selection",
  sessionType: "Session type selection",
};

enum RescheduleAppointmentEvent {
  RescheduleAvailabilitySelection = "Reschedule availability selection clicked",
  RescheduleConfirmBooking = "Reschedule book appointment clicked",
  RescheduleContinueCTAClicked = "Reschedule continue CTA clicked",
  RescheduleLeaveBookingModal = "Reschedule leave booking CTA clicked",
  RescheduleRecurrenceSelection = "Reschedule recurrence selection clicked",
}

const useBookingAnalytics = () => {
  const { track, trackPageView } = useAnalytics();

  const trackStepPageView = (step: BookingStep) => {
    const screenName = AnalyticsScreenNameMap[step];
    trackPageView(screenName);
  };

  const trackAlertView = (alert: BookingAlertType) => {
    trackPageView(BookingAlertNameMap[alert]);
  };

  const trackAlertEvent = ({ event, source }: TrackAlertEventArgs) => {
    track(event, { source: BookingAlertNameMap[source] });
  };

  const trackEvent = ({ event, source, value }: TrackEventArgs) => {
    const props: MixpanelProps = {};
    const analyticsScreenName = AnalyticsScreenNameMap[source];
    props.source = `${analyticsScreenName} screen`;
    if (value) {
      props.value = value;
    }
    track(event, props);
  };

  const trackStartBooking = (source: ValueOf<typeof TabSources>) => {
    track(BookingAnalyticsEvent.StartBooking, {
      source,
    });
  };

  const trackLearnMore = (source: ValueOf<typeof TabSources>) => {
    track(BookingAnalyticsEvent.LearnMore, {
      source,
    });
  };

  const trackEditEventPress = ({
    eventId: _eventId,
    isGroupClassEvent,
    isRecurring,
  }: {
    eventId: string;
    isGroupClassEvent: boolean;
    isRecurring?: boolean;
  }) => {
    const event = isGroupClassEvent
      ? EditEventAnalyticsEvent.EditGroupEventPressed
      : EditEventAnalyticsEvent.EditAppointmentPressed;

    const props: MixpanelProps = {
      [AnalyticsFieldName.IsRecurringEvent]: isRecurring,
    };

    track(event, props);
  };

  const trackRescheduleEvent = ({
    event,
    isEditingSeries,
    isRecurringEvent,
    source,
  }: {
    event: RescheduleAppointmentEvent;
    isEditingSeries: boolean;
    isRecurringEvent: boolean;
    source: BookingStep;
  }) => {
    const props: MixpanelProps = {
      [AnalyticsFieldName.IsEditingSeries]: isEditingSeries,
      [AnalyticsFieldName.IsRecurringEvent]: isRecurringEvent,
    };
    const analyticsScreenName = AnalyticsScreenNameMap[source];
    props.source = `${analyticsScreenName} screen`;
    track(event, props);
  };

  return {
    trackAlertEvent,
    trackAlertView,
    trackEditEventPress,
    trackEvent,
    trackLearnMore,
    trackRescheduleEvent,
    trackStartBooking,
    trackStepPageView,
  };
};

type UseBookingAnalytics = ReturnType<typeof useBookingAnalytics>;

export type { UseBookingAnalytics };
export { RescheduleAppointmentEvent };
export default useBookingAnalytics;
